import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store/rootReducer";

export type ModalStatus = "modal" | "form" | "hide" | "edit" | "add";
export type RightPanelStatus = "show" | "hide";

interface FormConfig {
  onSubmit?: (data: any) => void;
  defaultValues?: any;
  submitText?: string;
}

interface ModalProps {
  name?: string;
  status?: any;
  key?: any;
  onClose?: (name: string) => void;
  title?: string;
  children?: React.ReactNode;
  className?: string;
  header?: React.ReactNode;
  outerClassName?: string;
  form?: FormConfig;
  formLoading?: boolean;
  data?: any;
  url?: string | null;
}

export interface RightPanelActionButton {
  label: string;
  onClick?: () => void;
  href?: string;
  variant?: "primary" | "secondary";
  disabled?: boolean;
}

export interface RightPanelProps {
  name?: string;
  status?: RightPanelStatus;
  title?: React.ReactNode;
  data?: any;
  children?: React.ReactNode | ((panelData: any) => React.ReactNode);
  className?: string;
  bodyClassName?: string;
  showOverlay?: boolean;
  closeOnOverlayClick?: boolean;
  actionButton?: RightPanelActionButton;
}

interface UIState extends ModalProps {
  quoteKey: number;
  stepIndex: number;
  rightPanel: RightPanelProps;
}

const initialState: UIState = {
  name: "",
  status: "hide",
  onClose: () => {},
  title: "",
  children: null,
  className: "",
  header: undefined,
  outerClassName: "",
  quoteKey: 0,
  stepIndex: 1,

  form: {
    onSubmit: undefined,
    defaultValues: null,
    submitText: "",
  },

  formLoading: false,
  data: null,
  url: null,

  // Right Side Panel
  rightPanel: {
    name: "",
    status: "hide",
    title: "",
    data: null,
    className: "",
    bodyClassName: "",
    showOverlay: true,
    closeOnOverlayClick: true,
  },
};

const uiSlice = createSlice({
  name: "ui",
  initialState,

  reducers: {
    // -------------------------
    // Modal
    // -------------------------
    openModal(state, action: PayloadAction<ModalProps>) {
      state.name = action.payload.name;
      state.status = action.payload.status || "modal";
      state.onClose = action.payload.onClose || (() => {});
      state.title = action.payload.title || "";
      state.children = action.payload.children || null;
      state.data = action.payload.data || null;
      state.className = action.payload.className || "";
      state.header = action.payload.header || undefined;
      state.outerClassName = action.payload.outerClassName || "";
      state.form = action.payload.form ?? initialState.form;
      state.url = action.payload.url || null;
    },

    closeModal(state) {
      state.status = "hide";
      state.name = "";
    },

    // -------------------------
    // Step
    // -------------------------
    setStepIndex(state, action: PayloadAction<number>) {
      state.stepIndex = action.payload;
    },

    // -------------------------
    // Form Loading
    // -------------------------
    setFormLoading(state, action: PayloadAction<boolean>) {
      state.formLoading = action.payload;
    },

    // -------------------------
    // Right Side Panel
    // -------------------------
    openRightPanel(state, action: PayloadAction<RightPanelProps>) {
      state.rightPanel = {
        ...state.rightPanel,
        ...action.payload,
        status: "show",
      };
    },

    closeRightPanel(state) {
      state.rightPanel.status = "hide";
      state.rightPanel.name = "";
      state.rightPanel.data = null;
    },
  },
});

export const {
  openModal,
  closeModal,
  setStepIndex,
  setFormLoading,
  openRightPanel,
  closeRightPanel,
} = uiSlice.actions;

// Selectors
export const selectModal = (state: RootState) => state.ui;
export const selectRightPanel = (state: RootState) => state.ui.rightPanel;

export const { reducer: uiReducer } = uiSlice;
